import { personText } from '@/shared/utils/tableControls';
import { modalityText as positionModalityText } from '@/shared/utils/positionText';
import { STUDENT_APPLICATION_PILL } from '@/shared/utils/statusPills';
import {
    requestInstructorLabel,
    requestInstructorName,
} from '@/shared/utils/requestInstructor';
import { otherRoleInterestText } from '@/features/applications/student/programs/laAnswers';
import { formatAnswerDay, formatWindowDate } from '@/shared/utils/deadline';

// Column builders shared by the coordinator tables. Each column's `csv` is the
// text its cell shows, so an export carries what the screen carries.

// A moment (submitted, updated) reads as its Mountain time day
export const formatDate = formatAnswerDay;

// A calendar date (an assignment's start date) is stored as UTC midnight, so
// it is read in UTC; local time would show the day before west of Greenwich.
export const formatCalendarDate = formatWindowDate;

export const courseCode = (course) =>
    course?.subject || course?.courseNumber
        ? `${course.subject ?? ''} ${course.courseNumber ?? ''}`.trim()
        : '';

export const courseText = (course, section = null) =>
    [courseCode(course), section && `Section ${section}`, course?.title]
        .filter(Boolean)
        .join(' ');

export const personColumn = (key, label, getPerson, fallback = 'Unknown') => ({
    key,
    label,
    type: 'text',
    value: (row) => getPerson(row)?.name || fallback,
    csv: (row) =>
        personText(getPerson(row)?.name || fallback, getPerson(row)?.email),
});

export const courseColumn = (getCourse, getSection = () => null) => ({
    key: 'course',
    label: 'Course',
    type: 'text',
    value: (row) => courseCode(getCourse(row)),
    csv: (row) => courseText(getCourse(row), getSection(row)),
});

const lower = (value) =>
    typeof value === 'string' ? value.trim().toLowerCase() : '';

// Who teaches the section, by the one rule the Active LAs tab shares
// (requestInstructor.js): name null when an on-behalf request names nobody.
// The address follows the same person.
export function instructorOf(app) {
    const name = requestInstructorName(app);
    if (app?.isSubmittedOnBehalf === true) {
        const typed = app.courseConfirmation || {};
        return {
            name,
            email:
                app.instructorOfRecord?.email ||
                typed.instructorOfRecordEmail ||
                null,
        };
    }
    return { name, email: app?.applicant?.email || null };
}

// The submitter, shown only when it is someone other than the instructor.
// Two people can share a name, so only an account id or an email makes them
// one person.
export function submitterIfDifferent(app) {
    if (app?.isSubmittedOnBehalf !== true) return null;
    const instructor = instructorOf(app);
    const applicant = app.applicant || {};
    const sameAccount =
        app.instructorOfRecord?._id &&
        applicant._id &&
        String(app.instructorOfRecord._id) === String(applicant._id);
    const sameEmail =
        lower(instructor.email) &&
        lower(instructor.email) === lower(applicant.email);
    return sameAccount || sameEmail
        ? null
        : { name: applicant.name || 'Unknown', email: applicant.email };
}

// What the Faculty cell shows: the instructor's name, else "Instructor TBD"
export const instructorLabel = (app) => requestInstructorLabel(app);

// One column for both people: the instructor of record, with the submitter
// under it when they differ. Its text carries both, so search and the CSV
// match either.
export const facultyColumn = () => ({
    key: 'faculty',
    label: 'Faculty',
    type: 'text',
    value: instructorLabel,
    csv: (app) => {
        const instructor = instructorOf(app);
        const submitter = submitterIfDifferent(app);
        const text = personText(instructorLabel(app), instructor.email);
        return submitter
            ? `${text}; Submitted by ${personText(submitter.name, submitter.email)}`
            : text;
    },
});

// "Other" keeps the faculty member's own words
export const modalityText = (app) => {
    const confirmation = app?.courseConfirmation;
    if (!confirmation?.modality) return '';
    return confirmation.modality === 'other'
        ? confirmation.otherModality?.trim() || 'Other'
        : positionModalityText(confirmation.modality);
};

// `secondary` is the breakpoint the column appears from; below it the table
// shows modality under the course title (ModalityNote)
export const modalityColumn = (secondary = 'lg') => ({
    key: 'modality',
    label: 'Modality',
    type: 'text',
    secondary,
    value: modalityText,
});

// Filter value for requests whose course lists no department
export const NO_DEPARTMENT = '__none__';

export const departmentOf = (course) =>
    course?.department?.trim() || NO_DEPARTMENT;

// Options for a department filter, from the courses the rows hold, or null
// when they offer fewer than two choices so there is nothing to split. Rows
// with no department get a last option of their own, so they stay reachable.
export function departmentOptionsFor(rows, getCourse) {
    const found = new Set(rows.map((row) => departmentOf(getCourse(row))));
    const options = [...found]
        .filter((value) => value !== NO_DEPARTMENT)
        .sort((a, b) => a.localeCompare(b, 'en'))
        .map((value) => ({ value, label: value }));
    if (found.has(NO_DEPARTMENT)) {
        options.push({ value: NO_DEPARTMENT, label: 'No department listed' });
    }
    return options.length < 2 ? null : options;
}

export const dateColumn = (key, label, getDate, format = formatDate) => ({
    key,
    label,
    type: 'date',
    value: getDate,
    csv: (row) => (getDate(row) ? format(getDate(row)) : ''),
});

export const statusColumn = (order, getStatus, getLabel, label = 'Status') => ({
    key: 'status',
    label,
    type: 'status',
    order,
    value: getStatus,
    csv: (row) => getLabel(row) || getStatus(row) || '',
});

export const sessionsText = (sessions) =>
    (sessions || []).map((s) => `${s.day} ${s.time}`).join('; ');

// A select's options from the statuses the rows hold, in workflow order, or
// null when the rows hold fewer than two so there is nothing to split.
export function statusOptionsFor(rows, getStatus, order, labelOf) {
    const present = [...new Set(rows.map(getStatus).filter(Boolean))];
    if (present.length < 2) return null;
    const rank = (status) => {
        const at = order.indexOf(status);
        return at === -1 ? order.length : at;
    };
    return present
        .sort((a, b) => rank(a) - rank(b))
        .map((value) => ({ value, label: labelOf(value) }));
}

// Options for a "which course" filter, from the courses the rows hold, or
// null when the rows hold fewer than two so there is nothing to split.
export function courseOptionsFor(rows, getCourse) {
    const codes = [
        ...new Set(
            rows.map((row) => courseCode(getCourse(row))).filter(Boolean)
        ),
    ].sort((a, b) => a.localeCompare(b, 'en', { numeric: true }));
    return codes.length < 2
        ? null
        : codes.map((code) => ({ value: code, label: code }));
}

export const ASSIGNMENT_STATUS_ORDER = [
    'pending',
    'active',
    'completed',
    'terminated',
];

export const assignmentStatusLabel = (status) =>
    status === 'active' ? 'Active' : status;

// A status filter offers the words the rows' pills show
export const pillOptions = (pills, values) =>
    values.map((value) => ({ value, label: pills[value].label }));

export const STUDENT_APPLICATION_STATUS_ORDER = [
    'draft',
    'submitted',
    'under_review',
    'interview_scheduled',
    'offered',
    'accepted',
    'declined',
    'rejected',
    'withdrawn',
];

export const STUDENT_APPLICATION_STATUS_OPTIONS = pillOptions(
    STUDENT_APPLICATION_PILL,
    [
        'submitted',
        'under_review',
        'interview_scheduled',
        'offered',
        'accepted',
        'declined',
        'rejected',
    ]
);

const preferenceCodes = (app) =>
    (app.coursePreferences || [])
        .map((pref) =>
            `${pref.course?.subject || ''} ${pref.course?.courseNumber || ''}`.trim()
        )
        .join(', ');

// The Student Applicants table of the SI and LA dashboards. `statusMap` maps
// a status to the { label } the dashboard's badge shows.
export const studentApplicationColumns = (statusMap, choiceSummary) => [
    {
        key: 'student',
        label: 'Student Name',
        type: 'text',
        value: (app) => app.student?.name || 'Unknown',
    },
    {
        key: 'email',
        label: 'Email',
        type: 'text',
        secondary: true,
        value: (app) => app.student?.email,
    },
    {
        key: 'positions',
        label: 'Positions',
        type: 'text',
        value: (app) =>
            app.coursePreferences?.length > 0
                ? preferenceCodes(app)
                : choiceSummary(app),
    },
    {
        ...dateColumn('submitted', 'Submitted', (app) => app.submittedAt),
        secondary: true,
    },
    statusColumn(
        STUDENT_APPLICATION_STATUS_ORDER,
        (app) => app.status,
        (app) => statusMap[app.status]?.label
    ),
];

const WORK_STUDY_TEXT = { yes: 'Yes', no: 'No', not_sure: 'Not sure' };

// The work-study answer as a coordinator reads it: the student's own word,
// marked unverified (the column's label says it is self-reported).
export const workStudyText = (app) => {
    const answer = app.campusEmployment?.workStudy;
    return answer
        ? `${WORK_STUDY_TEXT[answer] || answer}, to be verified`
        : 'Not answered';
};

// The LA Student Applicants table: the shared columns, then the two answers
// the LA program reads beside them.
export const laStudentApplicationColumns = (statusMap, choiceSummary) => [
    ...studentApplicationColumns(statusMap, choiceSummary),
    {
        key: 'workStudy',
        label: 'Work-study (self-reported)',
        type: 'text',
        secondary: 'lg',
        value: workStudyText,
    },
    {
        key: 'otherRoles',
        label: 'Also interested in',
        type: 'text',
        secondary: 'lg',
        value: (app) => otherRoleInterestText(app.laDetails?.otherRoleInterest),
    },
];
